import React, { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useFormik } from 'formik'

import EditForm from '../../../../../../features/components/Forms/EditForm'
import PlacementsSectionContent from '../../../PlacementsSectionContent'

import { formatPlacementPositionsToFE } from '../../formatters'
import { transformValuesToPlacementBE } from '../../fields'
import { usePurifiedFormik } from '../../../../../../hooks/formHooks/usePurifiedFormik'
import { getActivePlacementsInitialValues } from '../../../../placements/helpers'

import { clearUpdateLineItem, updateLineItem } from '../../../../../../modules/actions/lineItems'
import {
  lineItemUpdateErrorSelector,
  lineItemUpdateIsLoadingSelector,
  lineItemWasUpdatedSelector
} from '../../../../../../modules/selectors/lineItems'
import { campaignSelector } from '../../../../../../modules/selectors/campaigns'
import { selectedFacebookAdAccountSelector } from '../../../../../../modules/selectors/adAccounts'

import { PLACEMENT_POSITIONS } from '../../../../../Multiplatform/LineItemForms/fields'

import { FACEBOOK_PLATFORM } from '../../../../../../constants/selectLists/platformList'
import { HISTORY_META_EDIT_SECTION } from '../../../../../../constants/changeHistory'

const PlacementSectionForm = ({ editItemData: lineItem, title, ...formProps }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const lineItemWasUpdated = useSelector(lineItemWasUpdatedSelector)
  const { id: selectedAdAccountId } = useSelector(selectedFacebookAdAccountSelector)
  const { objective: campaignObjective } = useSelector(campaignSelector)

  const handleSubmit = useCallback(
    values => {
      const updateLineItemData = {
        account: selectedAdAccountId,
        campaign_objective: campaignObjective,
        ...transformValuesToPlacementBE(values, campaignObjective),
        meta: {
          [HISTORY_META_EDIT_SECTION]: title
        }
      }

      dispatch(updateLineItem(updateLineItemData, lineItem.id, FACEBOOK_PLATFORM))
    },
    [dispatch, lineItem.id, campaignObjective, selectedAdAccountId, title]
  )

  const initialActivePlacementsValues = getActivePlacementsInitialValues(campaignObjective)

  const initialValues = useMemo(() => {
    return {
      [PLACEMENT_POSITIONS]: lineItem[PLACEMENT_POSITIONS]?.length
        ? formatPlacementPositionsToFE(lineItem[PLACEMENT_POSITIONS])
        : // if lineItem has no placements, it means all placements are selected in create form
          initialActivePlacementsValues
    }
  }, [lineItem, initialActivePlacementsValues])

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    onSubmit: handleSubmit
  })

  const purifiedFormik = usePurifiedFormik(formik)

  const handleClearUpdateLineItem = useCallback(() => {
    dispatch(clearUpdateLineItem())
  }, [dispatch])

  return (
    <EditForm
      formik={purifiedFormik}
      initialValues={initialValues}
      successSubmit={lineItemWasUpdated}
      clearEditItem={handleClearUpdateLineItem}
      errorSelector={lineItemUpdateErrorSelector}
      isLoadingSelector={lineItemUpdateIsLoadingSelector}
      {...formProps}
    >
      <p>{t('Select your custom combination of placements below:')}</p>
      <PlacementsSectionContent formik={purifiedFormik} />
    </EditForm>
  )
}

export default PlacementSectionForm
